import * as React from 'react';
import { AuthState } from '../auth/auth.types';
import { AdminState } from '../admin/admin.types';
import RaisedButton from 'material-ui/RaisedButton';
import { SignupState } from '../admin/signup.types';
import CreateEditModalComponent from './CreateEditModalComponent';
import { CREATE_MODE, EDIT_MODE, INITIAL_REQUEST } from './Constants';
import { AccountModalMode } from './Types';
import { Account } from '../account/Types';
import { STAFF } from '../account/Constants';
import { BusinessState, BusinessResponse } from '../business/business.types';
import { StaffRequest } from './Types';
import { isItStaffAccount } from '../util/permission.util';
import { isAuthTokenExpired } from '../commons/AuthTokenValidator';
import ReactTable from 'react-table';
import CommonFeatures from '../commons/CommonFeatures';

type TableData = {
  readonly email: string
  readonly firstName: string
  readonly lastName: string
  readonly phone: string
  readonly status: string
};

type Props = {
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly signupState: SignupState
  readonly businessState: BusinessState
  readonly checkEmailAvailability: (email: string) => Promise<Response>
  readonly registerStaffAccount: (request: StaffRequest) => Promise<Response>
  readonly updateStaffAccount: (request: StaffRequest) => Promise<Response>
  readonly deleteStaffAccount: (request: StaffRequest) => Promise<Response>
  readonly getBusiness: () => Promise<BusinessResponse>
};

type States = {
  readonly loading: boolean
  readonly modalOpen: boolean
  readonly modalMode: AccountModalMode
  readonly accounts: Array<Account>
  readonly staffRequest: StaffRequest
  readonly edited: StaffRequest
};

class StaffComponent extends React.Component<Props, States> {
  
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      modalOpen: false,
      modalMode: CREATE_MODE,
      accounts: this.getStaffAccounts(props),
      staffRequest: INITIAL_REQUEST,
      edited: INITIAL_REQUEST
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps && !isAuthTokenExpired() && nextProps.authState.statusCode === 200) {
      this.setState({
        accounts: this.getStaffAccounts(nextProps)
      });
    }
  }

  componentWillUnmount() {
    this.setState({modalOpen: false});
  }

  render() {
    const { authState, adminState } = this.props;
    const { staffRequest, modalOpen, modalMode, edited } = this.state;

    return (
      <CommonFeatures
        authState={this.props.authState}
        businessState={this.props.businessState}
      >
        <div className="page-header-row row mt-2">
          <div className="col-auto">
            <h3>Staffs</h3>
          </div>
        </div>
        <div className="row mb-2 justify-content-end animated fadeIn">
          <div className="col-auto">
            <div className="float-right">
              <RaisedButton
                label="Create"
                primary={true}
                onClick={this.openCreateModal}
                disabled={isItStaffAccount(authState, adminState)}
              />
            </div>
          </div>
        </div>
        <div className="row mb-2 mt-2 animated fadeIn">
          <div className="col-12">
            {this.renderStaffTable()}
          </div>
        </div>
        <CreateEditModalComponent
          requestChangeHandler={this.requestChangeHandler}
          modalOpenChangeHandler={this.modalOpenChangeHandler}
          mode={modalMode}
          modalOpen={modalOpen}
          staffRequest={staffRequest}
          edited={edited}
          checkEmailAvailability={this.props.checkEmailAvailability}
          signupState={this.props.signupState}
          registerStaffAccount={this.props.registerStaffAccount}
          updateStaffAccount={this.props.updateStaffAccount}
          getBusiness={this.props.getBusiness}
          deleteStaffAccount={this.props.deleteStaffAccount}
          authState={this.props.authState}
          adminState={this.props.adminState}
        />
      </CommonFeatures>
    );
  }

  private renderStaffTable = (): JSX.Element => {
    return(
      <ReactTable
        data={this.state.accounts.map((a: Account) => this.createTableData(a))}
        columns={[
          {
            accessor: 'email',
            Header: 'Email',
            style: {textAlign: 'center'}
          },
          {                                                                                       
            accessor: 'firstName',
            Header: 'First Name',
            style: {textAlign: 'center'}
          },
          {
            accessor: 'lastName',
            Header: 'Last Name',
            style: {textAlign: 'center'}
          },
          {
            accessor: 'phone',
            Header: 'Phone',
            style: {textAlign: 'center'}
          },
          {
            accessor: 'status',
            Header: 'Status',
            style: {textAlign: 'center'}
          }
        ]}
        defaultPageSize={20}
        className="-striped -highlight"
        keyField={'paymentHistoryId'}
        getTdProps={(state, rowInfo, column, instance) => {
          return {
            onClick: (e, handleOriginal) => rowInfo ? this.handleCellClick(rowInfo.index) : null
          };
        }}
      />
    );
  }

  private createTableData = (a: Account): TableData => {
    return({
      email: a.email,
      firstName: a.firstName,
      lastName: a.lastName,
      phone: a.phone,
      status: a.cancelled ? 'INACTIVE' : 'ACTIVE'
    });
  }

  private getStaffAccounts = (props: Props): Array<Account> => {
    return props.businessState.business ?
      props.businessState.business.accounts
        .filter((a: Account) => a && a.accountType ? a.accountType === STAFF : false) :
      [];
  }
  
  private handleCellClick = (row: number): void => {
    if (isItStaffAccount(this.props.authState, this.props.adminState)) {
      return;
    } else {
      const acc = this.state.accounts[row];
      
      this.setState({
        edited: {
          id: acc.id,
          firstName: acc.firstName,
          lastName: acc.lastName,
          email: acc.email,
          phone: acc.phone,
          password: '',
          confirmPassword: '',
          authorities: acc.authorities
        },
        staffRequest: {
          id: acc.id,
          firstName: acc.firstName,
          lastName: acc.lastName,
          email: acc.email,
          phone: acc.phone,
          password: '',
          confirmPassword: '',
          authorities: acc.authorities
        },
        modalOpen: true,
        modalMode: EDIT_MODE
      });
    }
  }

  private openCreateModal = (): void => this.setState({modalOpen: true, modalMode: CREATE_MODE});

  private requestChangeHandler = (request: StaffRequest, mode: AccountModalMode): void => {
    mode === CREATE_MODE ? this.setState({staffRequest: request}) : this.setState({edited: request});
  }

  private modalOpenChangeHandler = (open: boolean): void => this.setState({modalOpen: open});
}

export default StaffComponent;



// WEBPACK FOOTER //
// ./src/staff/StaffComponent.tsx